/**
 * PageShell — kale grid container die de Backstage `<Page>` grid-areas
 * vult (`pageHeader` / `pageSubheader` / `pageContent`). Wordt gebruikt
 * door compound-layouts zoals `PageDetailLayout` en `PageOverviewLayout`
 * om outer spacing/grid-eigendom te centraliseren.
 */

import type { ReactElement, ReactNode } from 'react';

import styles from './page-shell.module.css';

/** @public */
export interface PageShellProps {
  /** Hoofdinhoud — gevuld in de `pageContent` grid-area. */
  children?: ReactNode;
  /**
   * Optionele inhoud voor de `pageHeader` grid-area (titelbalk, breadcrumb).
   */
  header?: ReactNode;
  /**
   * Optionele inhoud voor de `pageNav` grid-area (linker side-nav).
   * Collapsed wanneer leeg (grid-template-columns `auto 1fr auto`).
   */
  nav?: ReactNode;
  /**
   * Optionele inhoud voor de `pageSidebar` grid-area (rechter sidebar).
   * Collapsed wanneer leeg.
   */
  sidebar?: ReactNode;
  /**
   * Optionele inhoud voor de `pageSubheader` grid-area (tabs).
   */
  subheader?: ReactNode;
}

/**
 * Render hulp die alleen een wrapper-div opent wanneer er content is.
 * Voorkomt lege div in de DOM (en daarmee onverwachte grid-cells die
 * niet collapsen).
 */
const Slot = ({
  area,
  children,
}: {
  area: string | undefined;
  children: ReactNode;
}): ReactElement | null => {
  if (children === undefined || children === null || children === false) {
    return null;
  }
  return (
    <div className={area} data-page-shell-slot={area}>
      {children}
    </div>
  );
};

/** @public */
export const PageShell = (props: PageShellProps) => {
  const { children, header, nav, sidebar, subheader } = props;

  // PageShell renders Fragment-like sibling wrappers — each wrapper carries
  // a `grid-area` CSS class that maps to the parent <Page>'s grid template.
  // There is intentionally no outer wrapper element, so PageShell does not
  // expose a `className` prop. A consumer that needs a custom outer
  // className must apply it to its own wrapper around PageShell.
  return (
    <>
      <Slot area={styles.header}>{header}</Slot>
      <Slot area={styles.subheader}>{subheader}</Slot>
      <Slot area={styles.nav}>{nav}</Slot>
      <Slot area={styles.content}>{children}</Slot>
      <Slot area={styles.sidebar}>{sidebar}</Slot>
    </>
  );
};
